"use client";

import { useCallback, useState } from "react";

/**
 * Activity-feed filter state, plus the localStorage / URL bookkeeping
 * that goes with it.
 *
 *  - `filter`: top-level segment ("all" / "following" / "smart" …).
 *    Persisted in the URL via `?filter=` so a refresh keeps the user
 *    on the same view. When the feed is `embedded` (homepage card),
 *    we skip the URL write — that surface is composed, not navigated.
 *  - `selectedCategories`: in-session category multi-select. Not
 *    persisted; resets on reload.
 *  - `minAmount`: minimum trade size filter, $. Persisted in
 *    localStorage so users don't have to re-set it each visit.
 *  - `viewMode`: "expanded" vs "compact" rows. Persisted in
 *    localStorage; same rationale.
 */
export function useActivityFilters(initialFilter: string, embedded: boolean) {
  const [filter, setFilterState] = useState(initialFilter);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Read localStorage synchronously in the initializer so the first render
  // already reflects the persisted value. A useEffect-driven hydration here
  // caused a visible flicker on refresh: render with `0` → effect runs →
  // re-render with saved value → feed re-filters.
  const [minAmount, setMinAmountState] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    const saved = window.localStorage.getItem("activityMinAmount");
    return saved ? parseInt(saved, 10) || 0 : 0;
  });
  const setMinAmount = useCallback((val: number) => {
    setMinAmountState(val);
    if (val > 0) localStorage.setItem("activityMinAmount", String(val));
    else localStorage.removeItem("activityMinAmount");
  }, []);

  const [viewMode, setViewModeState] = useState<"expanded" | "compact">(() => {
    if (typeof window === "undefined") return "expanded";
    const saved = window.localStorage.getItem("activityViewMode") as "expanded" | "compact" | null;
    return saved === "compact" || saved === "expanded" ? saved : "expanded";
  });
  const setViewMode = useCallback((mode: "expanded" | "compact") => {
    setViewModeState(mode);
    localStorage.setItem("activityViewMode", mode);
  }, []);

  // Update URL when filter changes — `history.replaceState` so we don't
  // trigger a Next.js re-render of the whole route.
  const setFilter = useCallback(
    (newFilter: string) => {
      setFilterState(newFilter);
      if (embedded) return; // Don't touch URL when embedded in homepage
      const params = new URLSearchParams(window.location.search);
      if (newFilter === "all") {
        params.delete("filter");
      } else {
        params.set("filter", newFilter);
      }
      const newUrl = params.toString() ? `?${params.toString()}` : "/activity";
      window.history.replaceState(null, "", newUrl);
    },
    [embedded],
  );

  return {
    filter,
    setFilter,
    selectedCategories,
    setSelectedCategories,
    minAmount,
    setMinAmount,
    viewMode,
    setViewMode,
  };
}
